import type { Json, Snapshot } from "../advisoryApi";
import { AllocationBar, BarList, CompositionView, SeriesChart, type Holding, type Pt } from "../charts";
import { isRecord, money, pct, toNumber } from "../format";

// One renderer per QP snapshotType (the data shapes of GET /snapshots). Each
// mirrors what QP's self-contained widget shows, drawn in Andersen's idiom.

function arr(v: unknown): Json[] {
  return Array.isArray(v) ? v.filter(isRecord) : [];
}

function pts(v: unknown): Pt[] {
  return arr(v)
    .map((p) => ({ date: String(p.date ?? ""), value: toNumber(p.value) ?? NaN }))
    .filter((p) => p.date && Number.isFinite(p.value));
}

function last(points: Pt[]): number | null {
  return points.length ? points[points.length - 1]!.value : null;
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" | null }) {
  return (
    <div className="perf-figure">
      <span className="label">{label}</span>
      <span className={tone ? `value ${tone}` : "value"}>{value}</span>
    </div>
  );
}

/**
 * A portfolio's holdings (the `portfolio` snapshot): donut or table, switched
 * by the shared Chart | Table control. The snapshot carries no asset classes;
 * pass `assetClassByIsin` (from the composition) to offer that grouping.
 */
export function HoldingsView({
  data,
  assetClassByIsin,
  title,
}: {
  data: Json;
  assetClassByIsin?: Record<string, string>;
  title?: string;
}) {
  const rows = arr(data.products);
  const currency = String(data.currency ?? "NOK");
  if (rows.length === 0) return <p className="muted">No holdings in this portfolio yet.</p>;
  const holdings: Holding[] = rows.map((p, i) => {
    const isin = p.isin ? String(p.isin) : null;
    return {
      key: String(p.productId ?? p.isin ?? i),
      name: String(p.productName ?? p.name ?? "—"),
      isin,
      assetClass: (isin && assetClassByIsin?.[isin]) || null,
      weight: toNumber(p.weight) ?? 0,
      amount: toNumber(p.amount),
    };
  });
  return <CompositionView title={title} holdings={holdings} currency={currency} table={<HoldingsTable rows={rows} data={data} currency={currency} />} />;
}

function HoldingsTable({ rows, data, currency }: { rows: Json[]; data: Json; currency: string }) {
  return (
    <table className="table adv-table-compact">
      <thead>
        <tr>
          <th>Fund</th>
          <th className="num">Weight</th>
          <th className="num">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={String(p.productId ?? p.isin ?? i)}>
            <td className="strong">
              {String(p.productName ?? p.name ?? "—")}
              {p.isin ? <div className="muted adv-isin">{String(p.isin)}</div> : null}
            </td>
            <td className="num">{pct(p.weight, 0)}</td>
            <td className="num">{money(p.amount, currency)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>{data.portfolioName ? `Total · ${String(data.portfolioName)}` : "Total"}</td>
          <td className="num">100 %</td>
          <td className="num">{money(data.totalAmount ?? rows.reduce((a, r) => a + (toNumber(r.amount) ?? 0), 0), currency)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

export function ForecastView({ data }: { data: Json }) {
  const currency = String(data.currency ?? "NOK");
  const expected = pts(data.forecast);
  const bank = pts(data.bank);
  const lower = pts(data.lower);
  const upper = pts(data.upper);
  return (
    <div className="adv-snapshot">
      <div className="perf-strip">
        <Figure label="Expected return" value={pct(data.expectedReturn, 2)} />
        <Figure label="Volatility" value={pct(data.volatility, 1)} />
        <Figure label="Expected value" value={money(last(expected), currency)} />
        <Figure label="Bank savings" value={money(last(bank), currency)} />
      </div>
      <SeriesChart
        unit={currency}
        ariaLabel="Portfolio forecast"
        series={[
          { label: "Expected", points: expected, kind: "main" },
          { label: `Bank savings (${pct(data.bankReturnPct, 0)})`, points: bank, kind: "compare" },
        ]}
        band={lower.length && upper.length ? { label: "Likely range (5–95 %)", lower, upper } : null}
      />
    </div>
  );
}

export function ExpectedValueView({ data }: { data: Json }) {
  const currency = String(data.currency ?? "NOK");
  const forecast = arr(data.forecast);
  const bank = arr(data.bank);
  // One row per year: the last month of each year in the series.
  const byYear = new Map<string, { value: number; bank: number | null; deposits: number }>();
  let deposits = 0;
  forecast.forEach((p, i) => {
    deposits += (toNumber(p.deposit) ?? 0) - (toNumber(p.withdrawal) ?? 0);
    const year = String(p.date ?? "").slice(0, 4);
    if (!year) return;
    byYear.set(year, { value: toNumber(p.value) ?? 0, bank: toNumber(bank[i]?.value), deposits });
  });
  const rows = [...byYear.entries()];
  return (
    <div className="adv-snapshot">
      <SeriesChart
        unit={currency}
        ariaLabel="Expected value"
        series={[
          { label: "Expected value", points: pts(data.forecast), kind: "main" },
          { label: "Bank savings", points: pts(data.bank), kind: "compare" },
        ]}
      />
      {rows.length > 0 && (
        <table className="table adv-table-compact">
          <thead>
            <tr>
              <th>Year</th>
              <th className="num">Net deposits</th>
              <th className="num">Expected value</th>
              <th className="num">Bank savings</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([y, r]) => (
              <tr key={y}>
                <td>{y}</td>
                <td className="num">{money(r.deposits, currency)}</td>
                <td className="num">{money(r.value, currency)}</td>
                <td className="num">{money(r.bank, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function HistoricalReturnView({ data }: { data: Json }) {
  const series = pts(data.timeseries);
  const first = series[0]?.value ?? null;
  const end = last(series);
  const total = first && end ? ((end - first) / first) * 100 : null;
  const years = series.length >= 2 ? (Date.parse(series[series.length - 1]!.date) - Date.parse(series[0]!.date)) / (365.25 * 86400000) : 0;
  const annual = total !== null && years > 0.5 ? (Math.pow(1 + total / 100, 1 / years) - 1) * 100 : null;
  const cov = isRecord(data.coverage) ? data.coverage : null;
  return (
    <div className="adv-snapshot">
      <div className="perf-strip">
        <Figure label="Total return" value={pct(total, 1)} tone={total === null ? null : total >= 0 ? "up" : "down"} />
        <Figure label="Per year" value={pct(annual, 1)} tone={annual === null ? null : annual >= 0 ? "up" : "down"} />
        <Figure label="Since" value={series[0]?.date ?? "—"} />
        {cov && <Figure label="Coverage" value={`${cov.coveredProducts ?? "?"} of ${cov.totalProducts ?? "?"} funds`} />}
      </div>
      <SeriesChart unit="" ariaLabel="Historical return, indexed to 100" series={[{ label: "Portfolio (indexed to 100)", points: series, kind: "main" }]} />
    </div>
  );
}

export function CostView({ data }: { data: Json }) {
  const currency = String(data.currency ?? "NOK");
  const yearly = arr(data.yearlyCosts);
  const first = isRecord(data.firstYearCost) ? data.firstYearCost : null;
  const withCost: Pt[] = yearly.map((y) => ({ date: `${y.year}-12-31`, value: toNumber(y.accountValue) ?? NaN })).filter((p) => Number.isFinite(p.value));
  const exCost: Pt[] = yearly.map((y) => ({ date: `${y.year}-12-31`, value: toNumber(y.accountValueExCost) ?? NaN })).filter((p) => Number.isFinite(p.value));
  const categories = arr(data.costCategorySummaries);
  const gap = (last(exCost) ?? 0) - (last(withCost) ?? 0);
  return (
    <div className="adv-snapshot">
      <div className="perf-strip">
        {first && <Figure label="First-year cost" value={money(first.totalCost, currency)} />}
        {first && <Figure label="Cost effect" value={pct(first.costEffectPct, 2)} />}
        <Figure label={`Total cost to ${yearly[yearly.length - 1]?.year ?? "end"}`} value={money(gap, currency)} />
      </div>
      <SeriesChart
        unit={currency}
        ariaLabel="Value before and after costs"
        series={[
          { label: "Value after costs", points: withCost, kind: "main" },
          { label: "Value without costs", points: exCost, kind: "compare" },
        ]}
      />
      {categories.length > 0 && (
        <table className="table adv-table-compact">
          <thead>
            <tr>
              <th>Cost</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c, i) => (
              <tr key={i}>
                <td>{String(c.cost_category ?? c.category ?? "—")}</td>
                <td className="num">{money(c.total, currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="num">{money(categories.reduce((a, c) => a + (toNumber(c.total) ?? 0), 0), currency)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

export function AllocationView({ data }: { data: Json }) {
  const slices = arr(data.allocations).map((a) => ({ label: String(a.assetClassName ?? "—"), pct: toNumber(a.percentage) ?? 0 }));
  const unclassified = toNumber(data.unclassifiedPct) ?? 0;
  if (unclassified > 0) slices.push({ label: "Unclassified", pct: unclassified });
  return <AllocationBar slices={slices} title="Asset allocation" />;
}

export function CostBenefitView({ data }: { data: Json }) {
  const p = isRecord(data.proposed) ? data.proposed : {};
  return (
    <div className="perf-strip">
      <Figure label="Expected return" value={pct(p.expectedReturnPct, 2)} />
      <Figure label="Volatility" value={pct(p.volatilityPct, 1)} />
      <Figure label="Product cost" value={pct(p.productCostPct, 2)} />
    </div>
  );
}

const STYLE_LABEL = ["Large value", "Large blend", "Large growth", "Mid value", "Mid blend", "Mid growth", "Small value", "Small blend", "Small growth"];

export function XrayView({ data, part }: { data: Json; part?: string }) {
  const kv = (list: unknown, key: string) => arr(list).map((r) => ({ label: String(r[key] ?? "—"), pct: toNumber(r.percentage) ?? 0 }));
  const style = new Map(arr(data.styleBox).map((s) => [String(s.style), toNumber(s.percentage) ?? 0]));
  const sections: Record<string, JSX.Element> = {
    allocation: <AllocationBar slices={kv(data.assetAllocation, "assetClass")} title="Asset classes" />,
    regions: <BarList rows={kv(data.regionalExposure, "region")} />,
    stock: <BarList rows={kv(data.globalStockSector, "sector")} />,
    bond: <BarList rows={kv(data.globalBondSector, "sector")} />,
    style: (
      <div className="adv-stylebox" role="table" aria-label="Equity style box">
        {STYLE_LABEL.map((label, i) => {
          const v = style.get(String(i + 1)) ?? 0;
          return (
            <div key={label} className="adv-stylebox-cell" style={{ background: `rgba(31, 52, 71, ${Math.min(v / 35, 1) * 0.85 + 0.04})`, color: v > 14 ? "var(--c-paper)" : "var(--c-ink)" }} title={label}>
              {v.toFixed(0)} %
            </div>
          );
        })}
      </div>
    ),
    holdings: (
      <table className="table adv-table-compact">
        <thead>
          <tr>
            <th>Underlying holding</th>
            <th>Sector</th>
            <th className="num">Weight</th>
          </tr>
        </thead>
        <tbody>
          {arr(data.topHoldings)
            .slice(0, 10)
            .map((h, i) => (
              <tr key={i}>
                <td className="strong">{String(h.name ?? "—")}</td>
                <td>{String(h.sector ?? "—")}</td>
                <td className="num">{pct(h.weight, 2)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    ),
  };
  if (part && sections[part]) return sections[part]!;
  return <div className="adv-snapshot">{Object.values(sections)}</div>;
}

/** Tab labels for QP's five X-ray components, in their tab order. */
export const XRAY_PARTS: { key: string; label: string }[] = [
  { key: "allocation", label: "Asset classes" },
  { key: "style", label: "Style box" },
  { key: "regions", label: "Regions" },
  { key: "stock", label: "Equity sectors" },
  { key: "bond", label: "Bond sectors" },
  { key: "holdings", label: "Top holdings" },
];

export function GenericSnapshotView({ data }: { data: Json }) {
  const entries = Object.entries(data).filter(([, v]) => typeof v === "number" || typeof v === "string");
  if (entries.length === 0) return <p className="muted">This result is available in the advice document.</p>;
  return (
    <dl className="info-grid">
      {entries.slice(0, 10).map(([k, v]) => (
        <div className="info-row" key={k}>
          <dt>{k}</dt>
          <dd>{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SnapshotView({
  snapshot,
  part,
  assetClassByIsin,
}: {
  snapshot: Snapshot;
  part?: string;
  /** For the holdings donut's "Asset class" grouping (the snapshot lacks it). */
  assetClassByIsin?: Record<string, string>;
}) {
  const d = snapshot.data ?? {};
  switch (snapshot.snapshotType) {
    case "portfolio":
      return <HoldingsView data={d} assetClassByIsin={assetClassByIsin} />;
    case "forecast":
      return <ForecastView data={d} />;
    case "expected_value":
    case "instance_expected_value":
      return <ExpectedValueView data={d} />;
    case "historical_return":
      return <HistoricalReturnView data={d} />;
    case "cost":
    case "instance_cost":
      return <CostView data={d} />;
    case "asset_allocation":
      return <AllocationView data={d} />;
    case "cost_benefit":
      return <CostBenefitView data={d} />;
    case "xray":
      return <XrayView data={d} part={part} />;
    default:
      return <GenericSnapshotView data={d} />;
  }
}

/** ISIN → asset class from a composition's positions (for the donut's grouping). */
export function assetClassesOf(positions: { isin: string | null; assetClassName?: string | null }[] | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of positions ?? []) if (p.isin && p.assetClassName) out[p.isin] = p.assetClassName;
  return out;
}
